import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import delve from 'dlv';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import Cookies from 'js-cookie';
import { RootState } from 'redux/rootReducer';
import useGeoLocation from 'react-ipgeolocation';

import { loadCountries } from 'redux/reducers/countries';
import { ICountryFlagProps } from 'types/shared';
import SearchableDropdown from 'components/base/Dropdown/SearchableDropdown';
import { saveUserLocation } from 'redux/reducers/user/updateUserLocation';
import {
  IUserSettingsState,
  loadOrInitUserSettings,
  shouldLoadUserSettings,
} from 'redux/reducers/userSettings';

import sortByDisplayName from 'helpers/sortByDisplayName';

import Checkbox from 'components/base/Checkbox/Checkbox';
import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import { ListItemBox } from 'components/base/TopicFiltersDropdown/CustomerDropdown';

import { Text, IconWrapper } from '../../styles';

const ConfirmLocationWrapper = styled('div')`
  position: relative;
`;
const DropdownWrapper = styled('div')<any>`
  max-width: 285px;
  position: ${({ withoutText }): string =>
    withoutText ? 'initial' : 'absolute'};
  top: 30px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const ListItem: React.FC<any> = (props) => {
  const { item, isActive, isHovered, isMultipleSelection } = props;
  return item ? (
    <ListItemBox
      isActive={isMultipleSelection ? false : isActive}
      isHovered={isHovered}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      paddingRight={2}
      paddingLeft={3}
      py={2}
    >
      {isMultipleSelection ? (
        <Box
          alignSelf="center"
          display="inline-block"
          marginLeft="2px"
          marginRight="-2px"
        >
          <Checkbox
            enabledText="Select"
            isChecked={!!isActive}
            insideDropdown
          />
        </Box>
      ) : null}
      {item?.icon && (
        <Box
          alignSelf="auto"
          display="inline-block"
          minWidth="2rem"
          maxHeight="1rem"
        >
          <IconWrapper>{item.icon}</IconWrapper>
        </Box>
      )}
      <Span marginLeft={1} textAlign="left">
        {item.label}
      </Span>
    </ListItemBox>
  ) : null;
};

interface IDropdownCountry {
  label: string;
  value: string;
}

const Location = forwardRef<any, { withoutText?: boolean }>(
  ({ withoutText }, ref) => {
    const dispatch = useDispatch();

    const [selectedCountry, setSelectedCountry] = useState<
      IDropdownCountry | undefined
    >({
      label: 'Select Country',
      value: '',
    });

    const locationInCookie = Cookies.get('user_location');
    const countries = useSelector((state: RootState) => state.countries);
    const sortedCountries = useMemo(
      () => countries?.data?.sort(sortByDisplayName),
      [countries]
    );
    const locationIP = useGeoLocation();

    const userSettings = useSelector(
      (state: RootState): IUserSettingsState => state.userSettings
    );

    const userSettingsLoaded = useMemo(
      () => delve(userSettings, ['success', 'all'], false),
      [userSettings]
    );

    const userCountryFromSettings = userSettings?.data?.userLocation;

    useEffect(() => {
      if (
        !selectedCountry?.value &&
        userCountryFromSettings &&
        countries.fulfilled === true &&
        countries.data
      ) {
        const countryFromSettings = {
          value: userCountryFromSettings,
          label:
            countries.data.find(
              (c: ICountryFlagProps) => c.id === userCountryFromSettings
            )?.displayName || '',
        };
        setSelectedCountry(countryFromSettings);
      }
    }, [userCountryFromSettings, countries.fulfilled]);

    const detectedCountry = useMemo(() => {
      if (
        locationIP &&
        locationIP.isLoading === false &&
        locationIP.country !== undefined &&
        countries.fulfilled === true &&
        countries.data
      ) {
        const countryDisplayName = countries.data.find(
          (c: ICountryFlagProps) => c.id === locationIP.country
        )?.displayName;
        return {
          id: locationIP.country,
          name: countryDisplayName,
        };
        return;
      } else {
        return undefined;
      }
    }, [locationIP, countries.fulfilled]);

    useEffect(() => {
      dispatch(loadCountries());

      if (dispatch(shouldLoadUserSettings())) {
        dispatch(loadOrInitUserSettings());
      }
    }, [dispatch]);

    const saveLocation = (newCountry: any): void => {
      const userCountryId = newCountry?.value;
      if (userCountryId) {
        dispatch(saveUserLocation(userCountryId));
      }
      const countryJSON = JSON.stringify({
        id: userCountryId,
        countryName: countries.data.find(
          (c: ICountryFlagProps) => c.id === userCountryId
        )?.displayName,
      });
      Cookies.set('user_location', countryJSON);
    };

    useEffect(() => {
      if (
        detectedCountry &&
        userCountryFromSettings &&
        detectedCountry.id === userCountryFromSettings &&
        !locationInCookie
      ) {
        const countryJSON = JSON.stringify({
          id: userCountryFromSettings,
          countryName: countries.data.find(
            (c: ICountryFlagProps) => c.id === userCountryFromSettings
          )?.displayName,
        });
        Cookies.set('user_location', countryJSON);
      }
    }, [userCountryFromSettings, detectedCountry, locationInCookie]);

    useEffect(() => {
      if (detectedCountry && userSettingsLoaded && !userCountryFromSettings) {
        saveLocation({
          value: detectedCountry?.id,
          label: detectedCountry?.name,
        });
      }
    }, [userCountryFromSettings, detectedCountry]);

    useImperativeHandle(ref, () => ({
      externalSave(): void {
        saveLocation(selectedCountry);
      },
    }));

    const onChange = (newRole: IDropdownCountry): void => {
      setSelectedCountry(newRole);
      if (!ref) {
        saveLocation(newRole);
      }
    };

    return countries ? (
      <ConfirmLocationWrapper>
        {!withoutText && (
          <>
            <Text>Your location is set to:</Text>
            <br />
          </>
        )}
        {sortedCountries && (
          <DropdownWrapper withoutText={withoutText}>
            <SearchableDropdown
              items={sortedCountries.map((c: ICountryFlagProps) => ({
                label: c.displayName,
                value: c.id,
              }))}
              selectedItem={
                selectedCountry || {
                  value: detectedCountry?.id,
                  label: detectedCountry?.name,
                }
              }
              onChange={onChange}
              Item={ListItem}
              disabled={false}
              isActiveGreen={false}
              isRoundedDropdown="all"
            />
          </DropdownWrapper>
        )}
      </ConfirmLocationWrapper>
    ) : null;
  }
);

export default Location;
